import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    IFlujoDefinicion,
    INodo,
    IOpcion,
    LIMITES_META_INTERACTIVO_UX,
    NODO_TIPO,
    crearNodoVacio,
    crearOpcionVacia,
} from 'src/app/models/notificacion/whatsapp_flujo.model';

/**
 * Construcción del único `FormGroup` reactivo del editor (design.md: "single
 * FormGroup owner", `ngModel` prohibido). Funciones puras, separadas del
 * componente que las usa, para poder probarlas sin `TestBed` y para que
 * `flujo-editor` (dueño del `FormGroup` raíz) y sus componentes
 * hijos (`flujo-nodo-lista`, `flujo-nodo-editor`) compartan exactamente la
 * misma forma al agregar un nodo u opción nueva.
 *
 * Los validadores acá son de FORMA únicamente (requerido, longitud máxima) —
 * la estructura (alcanzabilidad, terminación, handoff) SOLO la valida
 * `POST .../validar` en el backend; ver design.md, "Editor architecture".
 */

export const construirFormGroupOpcion = (fb: FormBuilder, opcion?: IOpcion): FormGroup => {
    const base = opcion || crearOpcionVacia('');

    return fb.group({
        id: [base.id, [Validators.required, Validators.maxLength(LIMITES_META_INTERACTIVO_UX.BOTON_ID_MAX_CARACTERES)]],
        etiqueta: [base.etiqueta, [Validators.required, Validators.maxLength(LIMITES_META_INTERACTIVO_UX.BOTON_ETIQUETA_MAX_CARACTERES)]],
        destinoNodoId: [base.destinoNodoId, Validators.required],
    });
};

export const construirFormGroupNodo = (fb: FormBuilder, nodo?: INodo): FormGroup => {
    const base = nodo || crearNodoVacio('');

    const formGroup = fb.group({
        id: [base.id, Validators.required],
        tipo: [base.tipo, Validators.required],
        texto: [base.texto, [Validators.required, Validators.maxLength(LIMITES_META_INTERACTIVO_UX.BODY_MAX_CARACTERES)]],
        siguienteNodoId: [base.siguienteNodoId],
        opciones: fb.array((base.opciones || []).map((opcion) => construirFormGroupOpcion(fb, opcion))),
    });

    return formGroup;
};

export const construirFormGroupVersion = (fb: FormBuilder, definicion?: IFlujoDefinicion): FormGroup => {
    const base: IFlujoDefinicion = definicion && definicion.nodos.length
        ? definicion
        : { nodoInicialId: '', nodos: [] };

    return fb.group({
        nodoInicialId: [base.nodoInicialId, Validators.required],
        nodos: fb.array(base.nodos.map((nodo) => construirFormGroupNodo(fb, nodo))),
    });
};

/** El `FormArray` de nodos del `FormGroup` raíz — tipado en un solo lugar. */
export const nodosFormArray = (versionFormGroup: FormGroup): FormArray => versionFormGroup.get('nodos') as FormArray;

/** El `FormArray` de opciones de un `FormGroup` de nodo. */
export const opcionesFormArray = (nodoFormGroup: FormGroup): FormArray => nodoFormGroup.get('opciones') as FormArray;

/**
 * Vacía/rellena `opciones` según el tipo elegido: solo `CHOICE` tiene
 * sentido con opciones (mismo criterio que `INodo.opciones` en el backend:
 * "Vacío salvo que tipo === CHOICE"). No borra silenciosamente datos de
 * quien ya las tenía cargadas y cambia de tipo por error: solo actúa al
 * pasar A CHOICE (agrega una opción vacía si no había ninguna) o AL SALIR de
 * CHOICE (limpia, porque `enviarInteractivo` en el backend no acepta
 * opciones en un nodo que no las envía).
 */
export const sincronizarOpcionesConTipo = (fb: FormBuilder, nodoFormGroup: FormGroup): void => {
    const tipo = nodoFormGroup.get('tipo')?.value;
    const opciones = opcionesFormArray(nodoFormGroup);

    if (tipo !== NODO_TIPO.CHOICE) {
        opciones.clear();
        return;
    }

    if (opciones.length === 0) {
        opciones.push(construirFormGroupOpcion(fb));
    }
};

/** Extrae la definición como el `IFlujoDefinicion` exacto que el backend espera. */
export const extraerDefinicionDeFormGroup = (versionFormGroup: FormGroup): IFlujoDefinicion => {
    return versionFormGroup.getRawValue() as IFlujoDefinicion;
};
